'use client';

import { useState } from 'react';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/navigation';
import { PageLayout } from '../../shared/layout/page/PageLayout';
import { Input } from '../../../components/common/input/Input';
import Button from '../../../components/common/button/Button';
import { Css, H4, InfoIcon, ITheme, Responsive, Tooltip } from '../../../ui';
import { Color } from '../../../styles/Color';
import { IPageEntry } from '../../../contentful/types/IPage';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { ErrorMessages } from '../../../utils/errorMessages';
import loginSchema from '../models/LoginSchema';
import { Topbar } from '../../../components/common/top-bar/Topbar';
import {
	POLICIES_SESSION_KEY,
	POLICIES_BADGE_KEY,
	POLICIES_LASTNAME_KEY,
	BADGE_TOOLTIP_TEXT,
} from '../constants';
import { useApi } from '../../../data/hooks/useApi';

export interface IRestrictedPoliciesLoginPageProps {
	page: IPageEntry;
	siteSection?: ISiteSectionEntry;
}

interface ILoginFormData {
	lastName: string;
	badgeNumber: string;
}

export default function RestrictedPoliciesLoginPage({
	page,
	siteSection,
}: IRestrictedPoliciesLoginPageProps) {
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<ILoginFormData>({
		mode: 'onSubmit',
		resolver: yupResolver(loginSchema),
	});

	const router = useRouter();
	const [showBadgeTooltip, setShowBadgeTooltip] = useState(false);

	const { handleApiData, loading, error } = useApi();

	const onSubmit = async (data: ILoginFormData) => {
		const trimmedData = {
			lastName: data.lastName.trim(),
			badgeNumber: data.badgeNumber.trim(),
		};

		const result = await handleApiData({
			url: '/api/badge-validation',
			method: 'POST',
			body: {
				badgeNumber: trimmedData.badgeNumber,
				lastName: trimmedData.lastName,
			},
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (result?.status === 'valid') {
			sessionStorage.setItem(POLICIES_SESSION_KEY, 'true');
			sessionStorage.setItem(POLICIES_BADGE_KEY, trimmedData.badgeNumber);
			sessionStorage.setItem(POLICIES_LASTNAME_KEY, trimmedData.lastName);
			router.push('/airport-business/policies');
		}
	};

	return (
		<PageLayout page={page} siteSection={siteSection}>
			<Topbar siteSectionId={siteSection?.fields.id} />
			<PageWrapper>
				<FormCard>
					<Title>Sign in to view policies</Title>
					<Subtitle>
						Enter your name and staff badge number to access airport policies
						and procedures
					</Subtitle>

					<StyledForm
						onSubmit={handleSubmit(onSubmit)}
						aria-label="Sign in to view policies"
						noValidate
					>
						<FieldGroup>
							<BadgeInputWrapper>
								<StyledInput
									label="Badge number*"
									{...register('badgeNumber')}
									isInvalid={!!errors.badgeNumber}
								/>
								<InfoIconWrapper>
									<InfoIconButton
										hideIcon
										onMouseEnter={() => setShowBadgeTooltip(true)}
										onMouseLeave={() => setShowBadgeTooltip(false)}
										onClick={() => setShowBadgeTooltip((prev) => !prev)}
										aria-label="Badge number info"
										iconPosition="right"
										icon={<StyledInfoIcon />}
									/>
									{showBadgeTooltip && (
										<BadgeTooltip position="bottom">
											{BADGE_TOOLTIP_TEXT}
										</BadgeTooltip>
									)}
								</InfoIconWrapper>
							</BadgeInputWrapper>
							{errors.badgeNumber && (
								<FormError>{errors.badgeNumber.message}</FormError>
							)}
						</FieldGroup>

						<FieldGroup>
							<StyledInput
								label="Last name*"
								{...register('lastName')}
								isInvalid={!!errors.lastName}
							/>
							{errors.lastName && (
								<FormError>{errors.lastName.message}</FormError>
							)}
						</FieldGroup>
						<ButtonWrapper>
							<StyledButton
								type="submit"
								disabled={loading}
								loading={loading}
								hideIcon
								aria-busy={loading}
							>
								Sign in
							</StyledButton>
						</ButtonWrapper>

						{error && (
							<FormSubmitError>
								{ErrorMessages.PoliciesLoginError}
							</FormSubmitError>
						)}
					</StyledForm>

					<HelpText>Can&apos;t sign in? Contact your supervisor</HelpText>
				</FormCard>
			</PageWrapper>
		</PageLayout>
	);
}

const PageWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: ${Css.pxToRem(80)} ${Css.pxToRem(16)};
	min-height: 70vh;
`;

const FormCard = styled.div<{}, ITheme>`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	max-width: ${Css.pxToRem(480)};
	padding: ${Css.pxToRem(32)};
	border: 1px solid ${Color.Neutral3};
	border-radius: ${Css.pxToRem(12)};
	background: ${Color.Neutral1};
`;

const Title = styled(H4)`
	margin: 0 0 ${Css.pxToRem(8)};
	text-align: center;
`;

const Subtitle = styled.p`
	margin: 0 0 ${Css.pxToRem(24)};
	text-align: center;
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(8)};
	width: 100%;

	label {
		left: ${Css.pxToRem(16)};
		color: ${Color.Neutral6};
	}
`;

const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledInput = styled(Input)<{}, ITheme>`
	padding-left: ${Css.pxToRem(16)};
	color: ${Color.OnBackground};
`;

const BadgeInputWrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
`;

const InfoIconWrapper = styled('div')`
	width: fit-content;
	height: fit-content;
	position: absolute;
	right: ${Css.pxToRem(12)};
	padding: 0;
`;

const InfoIconButton = styled(Button)`
	width: fit-content;
	padding: 0;
	background: none;
	border: none;

	&:hover:not(:disabled),
	&:focus:not(:disabled),
	&:hover:not(:disabled):not([aria-disabled='true']) {
		border: none;
		box-shadow: none;
		background: none;
	}
`;

const StyledInfoIcon = styled(InfoIcon)<{}, ITheme>`
	width: ${Css.pxToRem(24)};
	height: ${Css.pxToRem(24)};
	color: ${({ theme }) => theme.color.secondaryLight};
`;

const BadgeTooltip = styled(Tooltip)`
	white-space: normal;
	width: ${Css.pxToRem(280)};
	right: 0;
	text-align: left;
	padding: ${Css.pxToRem(8)};
	color: ${Color.Neutral2};
	background: ${Color.OnBackground};
	font-size: ${Css.pxToRem(14)};
	border-radius: ${Css.pxToRem(8)};
	transform: translateX(50%);
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin: ${Css.pxToRem(16)} 0;
`;

const StyledButton = styled(Button)`
	height: ${Css.pxToRem(45)};
	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
	}
`;

const FormError = styled.p`
	color: ${Color.Error};
	margin: ${Css.pxToRem(4)} 0 0;
	font-weight: 500;
	font-size: ${Css.pxToRem(12)};
`;

const FormSubmitError = styled.div`
	color: ${Color.Error};
	margin-bottom: ${Css.pxToRem(16)};
	text-align: center;
	font-weight: 500;
	font-size: ${Css.pxToRem(14)};
`;

const HelpText = styled.p`
	margin: 0;
	font-size: ${Css.pxToRem(14)};
`;
